// frontend/src/components/PostProduction/AudienceBehaviorPanel.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import RepeatIcon from '@mui/icons-material/Repeat';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import { DashboardFilters } from '../../types/PPDash';

interface RepeatCustomerData {
  totalUniqueCustomers: number;
  avgShowsPerCustomer: number;
  distribution: {
    oneShow: number;
    twoToFour: number;
    fivePlus: number;
    tenPlus: number;
  };
  repeatRate: number;
}

interface OrderPatternsData {
  totalOrders: number;
  totalTickets: number;
  avgTicketsPerOrder: number;
  distribution: {
    single: number;
    pair: number;
    smallGroup: number;
    largeGroup: number;
  };
  groupOrderPercentage: number;
}

interface AudienceBehaviorData {
  repeatCustomers: RepeatCustomerData;
  orderPatterns: OrderPatternsData;
}

interface AudienceBehaviorPanelProps {
  filters?: DashboardFilters;
}

const AudienceBehaviorPanel: React.FC<AudienceBehaviorPanelProps> = ({ filters }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AudienceBehaviorData | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        if (filters?.startDate) params.append('startDate', filters.startDate);
        if (filters?.endDate) params.append('endDate', filters.endDate);
        if (filters?.genre) params.append('genre', filters.genre);

        const response = await fetch(`/api/dashboard/figure?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const result = await response.json();

        if (result.success) {
          setData(result.data);
        } else {
          setError(result.error || 'Failed to load audience behavior data');
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filters]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  const { repeatCustomers, orderPatterns } = data;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Audience Loyalty & Booking Behavior
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Understanding repeat attendance and group booking patterns
      </Typography>

      <Grid container spacing={3}>
        {/* Repeat Customers Section */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ backgroundColor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RepeatIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Repeat Customer Analysis</Typography>
              </Box>

              {/* Key Metrics */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'action.hover', textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {repeatCustomers.avgShowsPerCustomer}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg shows per customer
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'action.hover', textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {repeatCustomers.repeatRate}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Return for multiple shows
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Distribution */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Customer Loyalty Tiers
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">One-time visitors</Typography>
                  <Chip 
                    label={repeatCustomers.distribution.oneShow} 
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {repeatCustomers.totalUniqueCustomers > 0 ? 
                    ((repeatCustomers.distribution.oneShow / repeatCustomers.totalUniqueCustomers) * 100).toFixed(1) 
                    : 0}% of audience
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Regulars (2-4 shows)</Typography>
                  <Chip 
                    label={repeatCustomers.distribution.twoToFour} 
                    size="small"
                    color="info"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {repeatCustomers.totalUniqueCustomers > 0 ? 
                    ((repeatCustomers.distribution.twoToFour / repeatCustomers.totalUniqueCustomers) * 100).toFixed(1) 
                    : 0}% of audience
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Superfans (5-9 shows)</Typography>
                  <Chip 
                    label={repeatCustomers.distribution.fivePlus} 
                    size="small"
                    color="success"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {repeatCustomers.totalUniqueCustomers > 0 ? 
                    ((repeatCustomers.distribution.fivePlus / repeatCustomers.totalUniqueCustomers) * 100).toFixed(1) 
                    : 0}% of audience
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Ultra-fans (10+ shows)</Typography>
                  <Chip 
                    label={repeatCustomers.distribution.tenPlus} 
                    size="small"
                    color="error"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {repeatCustomers.totalUniqueCustomers > 0 ? 
                    ((repeatCustomers.distribution.tenPlus / repeatCustomers.totalUniqueCustomers) * 100).toFixed(1) 
                    : 0}% of audience
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Total unique customers:</strong> {repeatCustomers.totalUniqueCustomers.toLocaleString()}
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Patterns Section */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ backgroundColor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <GroupsIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Group Booking Patterns</Typography>
              </Box>

              {/* Key Metrics */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'action.hover', textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {orderPatterns.avgTicketsPerOrder}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg tickets per order
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'action.hover', textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: 'info.main' }}>
                      {orderPatterns.groupOrderPercentage}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Group orders (4+ tickets)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Distribution */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Order Size Breakdown
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">Solo (1 ticket)</Typography>
                  </Box>
                  <Chip 
                    label={orderPatterns.distribution.single} 
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {orderPatterns.totalOrders > 0 ? 
                    ((orderPatterns.distribution.single / orderPatterns.totalOrders) * 100).toFixed(1) 
                    : 0}% of orders
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon fontSize="small" color="info" />
                    <Typography variant="body2">Pairs (2 tickets)</Typography>
                  </Box>
                  <Chip 
                    label={orderPatterns.distribution.pair} 
                    size="small"
                    color="info"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {orderPatterns.totalOrders > 0 ? 
                    ((orderPatterns.distribution.pair / orderPatterns.totalOrders) * 100).toFixed(1) 
                    : 0}% of orders
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon fontSize="small" color="warning" />
                    <Typography variant="body2">Small groups (3-4 tickets)</Typography>
                  </Box>
                  <Chip 
                    label={orderPatterns.distribution.smallGroup} 
                    size="small"
                    color="warning"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {orderPatterns.totalOrders > 0 ? 
                    ((orderPatterns.distribution.smallGroup / orderPatterns.totalOrders) * 100).toFixed(1) 
                    : 0}% of orders
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon fontSize="small" color="success" />
                    <Typography variant="body2">Large groups (5+ tickets)</Typography>
                  </Box>
                  <Chip 
                    label={orderPatterns.distribution.largeGroup} 
                    size="small"
                    color="success"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {orderPatterns.totalOrders > 0 ? 
                    ((orderPatterns.distribution.largeGroup / orderPatterns.totalOrders) * 100).toFixed(1) 
                    : 0}% of orders
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Total orders:</strong> {orderPatterns.totalOrders.toLocaleString()} • 
                  <strong> Total tickets:</strong> {orderPatterns.totalTickets.toLocaleString()}
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Insights & Recommendations */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Marketing Insights & Recommendations
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    Loyalty Strategy
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • {repeatCustomers.repeatRate}% return for multiple shows - focus on converting one-timers to regulars
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • {repeatCustomers.distribution.tenPlus} ultra-fans (10+ shows) are your evangelists - create VIP recognition
                  </Typography>
                  <Typography variant="body2">
                    • Average customer attends {repeatCustomers.avgShowsPerCustomer} shows - use this for membership pricing tiers
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    Group Booking Strategy
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • {orderPatterns.avgTicketsPerOrder} avg tickets/order - {orderPatterns.avgTicketsPerOrder >= 2 ? 'couples dominate' : 'solo buyers dominate'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • {orderPatterns.groupOrderPercentage}% are group orders (4+) - {orderPatterns.groupOrderPercentage > 20 ? 'consider group discounts' : 'focus on pair/couple messaging'}
                  </Typography>
                  <Typography variant="body2">
                    • {((orderPatterns.distribution.pair / orderPatterns.totalOrders) * 100).toFixed(0)}% buy in pairs - "Bring a friend" pricing makes sense
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AudienceBehaviorPanel;